import m, {Component, Vnode} from "mithril";

export type AttachmentSource = "upload"|"url";

export interface InterviewGoalAttachmentRow {
    rowKey: string;
    id: number|null;
    source: AttachmentSource;
    type: string|null;
    filename: string|null;
    downloadUrl: string|null;
    url: string;
    label: string;
}

export interface InterviewGoalRow {
    rowKey: string;
    id: number|null;
    description: string;
    attachments: InterviewGoalAttachmentRow[];
}

export interface InterviewGoalsEditorOptions {
    goals: InterviewGoalRow[];
    fileAccept: string;
    goalRemovalRestricted: boolean;
}

type Translations = {[key: string]: string};

export function goalFieldName(goalRowKey: string, field: string): string {
    return `goals[list][${goalRowKey}][${field}]`;
}

export function attachmentFieldName(
    goalRowKey: string,
    attachmentRowKey: string,
    field: string,
): string {
    return `goals[list][${goalRowKey}][attachments][${attachmentRowKey}][${field}]`;
}

function goalFieldId(goalRowKey: string, field: string): string {
    return `frm-goals-list-${goalRowKey}-${field}`;
}

function attachmentFieldId(
    goalRowKey: string,
    attachmentRowKey: string,
    field: string,
): string {
    return `frm-goals-list-${goalRowKey}-attachments-${attachmentRowKey}-${field}`;
}

export default class InterviewGoalsEditor implements Component {

    public readonly goals: InterviewGoalRow[];

    private keySequence = 0;

    public constructor(
        private readonly options: InterviewGoalsEditorOptions,
        private readonly translations: Translations,
    ) {
        this.goals = options.goals.map(goal => ({
            ...goal,
            attachments: goal.attachments.map(attachment => ({...attachment})),
        }));
    }

    public addGoal(): InterviewGoalRow {
        const goal: InterviewGoalRow = {
            rowKey: this.createRowKey(),
            id: null,
            description: "",
            attachments: [],
        };
        this.goals.push(goal);

        return goal;
    }

    public removeGoal(goalRowKey: string): void {
        const index = this.goals.findIndex(goal => goal.rowKey === goalRowKey);
        if (index !== -1) {
            this.goals.splice(index, 1);
        }
    }

    public moveGoal(goalRowKey: string, direction: -1|1): void {
        this.moveRow(this.goals, goalRowKey, direction);
    }

    public addAttachment(
        goalRowKey: string,
        source: AttachmentSource,
    ): InterviewGoalAttachmentRow|null {
        const goal = this.goals.find(row => row.rowKey === goalRowKey);
        if (!goal) {
            return null;
        }

        const attachment: InterviewGoalAttachmentRow = {
            rowKey: this.createRowKey(),
            id: null,
            source,
            type: null,
            filename: null,
            downloadUrl: null,
            url: "",
            label: "",
        };
        goal.attachments.push(attachment);

        return attachment;
    }

    public removeAttachment(goalRowKey: string, attachmentRowKey: string): void {
        const goal = this.goals.find(row => row.rowKey === goalRowKey);
        if (!goal) {
            return;
        }
        const index = goal.attachments.findIndex(attachment => attachment.rowKey === attachmentRowKey);
        if (index !== -1) {
            goal.attachments.splice(index, 1);
        }
    }

    public moveAttachment(
        goalRowKey: string,
        attachmentRowKey: string,
        direction: -1|1,
    ): void {
        const goal = this.goals.find(row => row.rowKey === goalRowKey);
        if (goal) {
            this.moveRow(goal.attachments, attachmentRowKey, direction);
        }
    }

    public view(): Vnode {
        return m(".interview-goals-editor.d-flex.flex-column.gap-3", [
            m(".d-flex.flex-column.gap-3", this.goals.map(
                (goal, index) => this.viewGoal(goal, index),
            )),
            m(".align-self-start", m("button.btn.btn-outline-primary", {
                type: "button",
                onclick: () => this.addGoal(),
            }, [
                m("i.bi.bi-plus-lg.me-1"),
                this.translate("add_goal"),
            ])),
        ]);
    }

    private viewGoal(goal: InterviewGoalRow, index: number): Vnode {
        return m(".interview-goal-editor.border-bottom.pb-3", {key: goal.rowKey}, [
            m("input", {
                type: "hidden",
                name: goalFieldName(goal.rowKey, "id"),
                id: goalFieldId(goal.rowKey, "id"),
                value: goal.id ?? "",
            }),
            m(".d-flex.flex-column.flex-md-row.align-items-md-start.gap-2", [
                m(".flex-grow-1", [
                    m("input.form-control", {
                        type: "text",
                        name: goalFieldName(goal.rowKey, "description"),
                        id: goalFieldId(goal.rowKey, "description"),
                        value: goal.description,
                        placeholder: this.translate("interview_goal_description"),
                        required: true,
                        oninput: (event: InputEvent) => {
                            goal.description = (event.currentTarget as HTMLInputElement).value;
                        },
                    }),
                ]),
                this.viewMoveAndRemoveButtons(
                    index,
                    this.goals.length,
                    () => this.moveGoal(goal.rowKey, -1),
                    () => this.moveGoal(goal.rowKey, 1),
                    () => this.removeGoal(goal.rowKey),
                    !(this.options.goalRemovalRestricted && goal.attachments.length > 0),
                ),
            ]),
            m(".mt-2.ms-md-4", [
                goal.attachments.length > 0
                    ? m(".d-flex.flex-column.gap-2.mb-2", goal.attachments.map(
                        (attachment, attachmentIndex) =>
                            this.viewAttachment(goal, attachment, attachmentIndex),
                    ))
                    : null,
                m(".d-flex.flex-wrap.gap-2", [
                    m("button.btn.btn-sm.btn-outline-primary", {
                        type: "button",
                        onclick: () => this.addAttachment(goal.rowKey, "upload"),
                    }, [
                        m("i.bi.bi-plus-lg.me-1"),
                        this.translate("interview_goal_attachment_add_file"),
                    ]),
                    m("button.btn.btn-sm.btn-outline-primary", {
                        type: "button",
                        onclick: () => this.addAttachment(goal.rowKey, "url"),
                    }, [
                        m("i.bi.bi-plus-lg.me-1"),
                        this.translate("interview_goal_attachment_add_link"),
                    ]),
                ]),
            ]),
        ]);
    }

    private viewAttachment(
        goal: InterviewGoalRow,
        attachment: InterviewGoalAttachmentRow,
        index: number,
    ): Vnode {
        return m(".interview-goal-attachment", {key: attachment.rowKey}, [
            m("input", {
                type: "hidden",
                name: attachmentFieldName(goal.rowKey, attachment.rowKey, "id"),
                id: attachmentFieldId(goal.rowKey, attachment.rowKey, "id"),
                value: attachment.id ?? "",
            }),
            m("input", {
                type: "hidden",
                name: attachmentFieldName(goal.rowKey, attachment.rowKey, "source"),
                id: attachmentFieldId(goal.rowKey, attachment.rowKey, "source"),
                value: attachment.source,
            }),
            m(".d-flex.flex-column.flex-lg-row.align-items-lg-start.gap-2", [
                m(".flex-grow-1", {style: {minWidth: 0}}, [
                    m(".input-group", [
                        m("span.input-group-text", m(`i.bi.${this.attachmentIcon(attachment)}`)),
                        this.viewAttachmentSource(goal, attachment),
                    ]),
                ]),
                m(".flex-grow-1", {style: {minWidth: 0}}, [
                    m("input.form-control", {
                        type: "text",
                        name: attachmentFieldName(goal.rowKey, attachment.rowKey, "label"),
                        id: attachmentFieldId(goal.rowKey, attachment.rowKey, "label"),
                        value: attachment.label,
                        maxlength: 500,
                        placeholder: this.translate("interview_goal_attachment_label"),
                        oninput: (event: InputEvent) => {
                            attachment.label = (event.currentTarget as HTMLInputElement).value;
                        },
                    }),
                ]),
                this.viewMoveAndRemoveButtons(
                    index,
                    goal.attachments.length,
                    () => this.moveAttachment(goal.rowKey, attachment.rowKey, -1),
                    () => this.moveAttachment(goal.rowKey, attachment.rowKey, 1),
                    () => this.removeAttachment(goal.rowKey, attachment.rowKey),
                ),
            ]),
        ]);
    }

    private viewAttachmentSource(
        goal: InterviewGoalRow,
        attachment: InterviewGoalAttachmentRow,
    ): Vnode {
        if (attachment.source === "url") {
            return m("input.form-control", {
                type: "url",
                name: attachmentFieldName(goal.rowKey, attachment.rowKey, "url"),
                id: attachmentFieldId(goal.rowKey, attachment.rowKey, "url"),
                value: attachment.url,
                maxlength: 2048,
                placeholder: this.translate("interview_goal_attachment_url"),
                required: true,
                oninput: (event: InputEvent) => {
                    attachment.url = (event.currentTarget as HTMLInputElement).value;
                },
            });
        }

        if (attachment.id !== null) {
            const filename = attachment.filename ?? this.translate("interview_goal_attachment_file");
            return m(".form-control.text-break", attachment.downloadUrl
                ? m("a.text-decoration-none", {
                    href: attachment.downloadUrl,
                    target: "_blank",
                    rel: "noopener",
                }, filename)
                : filename);
        }

        return m("input.form-control", {
            type: "file",
            name: attachmentFieldName(goal.rowKey, attachment.rowKey, "upload"),
            id: attachmentFieldId(goal.rowKey, attachment.rowKey, "upload"),
            accept: this.options.fileAccept,
            required: true,
        });
    }

    private viewMoveAndRemoveButtons(
        index: number,
        rowCount: number,
        moveUp: () => void,
        moveDown: () => void,
        remove: () => void,
        allowRemove = true,
    ): Vnode {
        return m(".btn-group.flex-shrink-0", [
            m("button.btn.btn-outline-secondary", {
                type: "button",
                title: this.translate("move_up"),
                "aria-label": this.translate("move_up"),
                disabled: index === 0,
                onclick: moveUp,
            }, m("i.bi.bi-arrow-up-short")),
            m("button.btn.btn-outline-secondary", {
                type: "button",
                title: this.translate("move_down"),
                "aria-label": this.translate("move_down"),
                disabled: index === rowCount - 1,
                onclick: moveDown,
            }, m("i.bi.bi-arrow-down-short")),
            allowRemove ? m("button.btn.btn-outline-danger", {
                type: "button",
                title: this.translate("delete"),
                "aria-label": this.translate("delete"),
                onclick: remove,
            }, m("i.bi.bi-trash-fill")) : null,
        ]);
    }

    private attachmentIcon(attachment: InterviewGoalAttachmentRow): string {
        const type = attachment.type ?? attachment.source;
        const icons: {[type: string]: string} = {
            image: "bi-image",
            audio: "bi-music-note-beamed",
            video: "bi-camera-video",
            document: "bi-file-earmark-text",
            link: "bi-link-45deg",
            url: "bi-link-45deg",
            upload: "bi-paperclip",
        };

        return icons[type] ?? "bi-paperclip";
    }

    private moveRow<T extends {rowKey: string}>(
        rows: T[],
        rowKey: string,
        direction: -1|1,
    ): void {
        const index = rows.findIndex(row => row.rowKey === rowKey);
        const targetIndex = index + direction;
        if (index < 0 || targetIndex < 0 || targetIndex >= rows.length) {
            return;
        }

        const [row] = rows.splice(index, 1);
        rows.splice(targetIndex, 0, row);
    }

    private createRowKey(): string {
        const existingKeys = new Set<string>();
        this.goals.forEach(goal => {
            existingKeys.add(goal.rowKey);
            goal.attachments.forEach(attachment => existingKeys.add(attachment.rowKey));
        });

        let rowKey: string;
        do {
            this.keySequence++;
            rowKey = `new_${this.keySequence}`;
        } while (existingKeys.has(rowKey));

        return rowKey;
    }

    private translate(key: string): string {
        return this.translations[key] ?? key;
    }
}
